import type { AcademicStanding } from '../types/verification'

/**
 * Academic standing, per person, as other surfaces are permitted to see it.
 *
 * This is deliberately a separate register rather than a field bolted onto each
 * person, roommate candidate or seller record. Two reasons:
 *
 *   1. Absence is meaningful. A key that is not here has NO academic
 *      verification record, which is different from having one that failed.
 *      Consumers render that as "not verified" rather than inventing a level.
 *   2. Verification is not a property of being a seller or a roommate. It
 *      belongs to the identity, and every surface reads the same record — so a
 *      person cannot appear verified in the marketplace and unverified in a
 *      community.
 *
 * These entries carry only what the trust record is allowed to expose. No
 * student identifier, no institutional email, no documents, no case notes.
 *
 * Seeded development data.
 */
const REGISTER: Record<string, AcademicStanding> = {
  /* ------------------------------------------------- community handles */
  amarachi: {
    level: 'institution_verified',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'B.Sc. Systems Engineering',
    academicLevel: '400 level',
    status: 'active_student',
    confirmedBy: 'University of Lagos — Registry',
  },
  tobi: {
    level: 'email_verified',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'B.Sc. Computer Science',
    academicLevel: '300 level',
    // Email access says nothing about enrolment, so the status stays
    // unestablished rather than being assumed from the address.
    status: 'unknown',
    confirmedBy: 'Approved institutional email domain',
  },
  chidera: {
    level: 'document_verified',
    state: 'verified',
    institution: 'Federal University of Technology, Akure',
    campus: 'Main Campus',
    programme: 'B.Eng. Mechanical Engineering',
    academicLevel: '300 level',
    status: 'active_student',
    confirmedBy: 'Campuzon verification team',
  },
  fatima: {
    level: 'institution_verified',
    state: 'expired',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'B.Sc. Economics',
    academicLevel: '300 level',
    status: 'active_student',
    confirmedBy: 'University of Lagos — Registry',
  },
  ifeanyi: {
    level: 'institution_verified',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'B.Sc. Accounting',
    academicLevel: null,
    status: 'alumni',
    confirmedBy: 'University of Lagos — Registry',
  },

  /* ------------------------------------------------ roommate candidates */
  'cand-1': {
    level: 'institution_verified',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'B.Sc. Accounting',
    academicLevel: '300 Level',
    status: 'active_student',
    confirmedBy: 'University of Lagos — Registry',
  },
  'cand-2': {
    level: 'email_verified',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'B.Eng. Civil Engineering',
    academicLevel: '400 Level',
    status: 'unknown',
    confirmedBy: 'Approved institutional email domain',
  },
  'cand-3': {
    level: 'institution_verified',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'B.Sc. Systems Engineering',
    academicLevel: '300 Level',
    status: 'active_student',
    confirmedBy: 'University of Lagos — Registry',
  },
  'cand-5': {
    level: 'document_verified',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'B.Sc. Computer Science',
    academicLevel: '300 Level',
    status: 'active_student',
    confirmedBy: 'Campuzon verification team',
  },
  'cand-6': {
    level: 'institution_verified',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'B.Pharm Pharmacy',
    academicLevel: '400 Level',
    status: 'active_student',
    confirmedBy: 'University of Lagos — Registry',
  },

  /* ------------------------------------------------ marketplace providers */
  tokoro: {
    level: 'institution_verified',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'M.A. English',
    academicLevel: null,
    status: 'graduated',
    confirmedBy: 'University of Lagos — Registry',
  },
  'ifeoma.e': {
    level: 'institution_integrated',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'Department of Systems Engineering',
    academicLevel: null,
    status: 'active_student',
    confirmedBy: 'University of Lagos — institutional connection',
  },
  danielu: {
    level: 'email_verified',
    state: 'verified',
    institution: 'Federal University of Technology, Akure',
    campus: 'Main Campus',
    programme: 'B.Tech Computer Science',
    academicLevel: '400 Level',
    status: 'unknown',
    confirmedBy: 'Approved institutional email domain',
  },
  gracen: {
    level: 'document_verified',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'B.Sc. Statistics',
    academicLevel: '400 Level',
    status: 'active_student',
    confirmedBy: 'Campuzon verification team',
  },

  /* -------------------------------------------- knowledge authors, by name */
  'F. Adeyemi': {
    level: 'institution_integrated',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'Department of Systems Engineering',
    academicLevel: null,
    status: 'active_student',
    confirmedBy: 'University of Lagos — institutional connection',
  },
  'K. Eze': {
    level: 'institution_verified',
    state: 'verified',
    institution: 'University of Lagos',
    campus: 'Akoka',
    programme: 'B.Sc. Systems Engineering',
    academicLevel: '400 level',
    status: 'active_student',
    confirmedBy: 'University of Lagos — Registry',
  },
  'B. Salami': {
    level: 'document_verified',
    state: 'verified',
    institution: 'Federal University of Technology, Akure',
    campus: 'Main Campus',
    programme: 'B.Eng. Mechanical Engineering',
    academicLevel: '300 level',
    status: 'active_student',
    confirmedBy: 'Campuzon verification team',
  },
}

/**
 * The standing for a person, or null when no academic verification record
 * exists at all. Callers must handle null as "nothing has been checked" rather
 * than defaulting to a level.
 */
export function standingFor(key: string | null | undefined): AcademicStanding | null {
  if (!key) return null
  return REGISTER[key] ?? REGISTER[key.replace(/^@/, '')] ?? null
}

/**
 * Which configured access policy governs a space. Membership requirements live
 * as policy records, never as conditions written into a component.
 */
export const SPACE_POLICY: Record<string, string> = {
  // Community groups
  'grp-1': 'pol-2',
  'grp-4': 'pol-1',
  // Social communities
  'com-1': 'pol-1',
  'com-6': 'pol-7',
}
